// Sugalarm — Alerts screen with two tabs: live alerts and predictions.
//
// 7-day window cap mirrors the design bundle's range; user can't widen
// it from the screen this cycle (the spec says "don't auto-broaden the
// window; let the user know what window they're looking at").
//
// CYCLE 42 — NOT IN THE NAV. The `Alerts` tab was retired from the
// EdgeRail / FooterTabs / mobile tab bar (see NavIcon.tsx `NAV_ITEMS`).
// This screen and its `<Route path="/alerts">` registration in App.tsx
// are deliberately preserved: a push-notification tap and the cycle-28
// `sugalarm://alerts?id=…` custom-URL scheme both deep-link here (the
// `?id=` row scroll + pulse below is the landing affordance). The
// alerts + predictions engines still run server-side unchanged —
// only the visible nav entry is gone.

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import * as alertsApi from "../../api/alerts";
import * as predictionsApi from "../../api/predictions";
import { Button } from "../../components/ui/Button";
import { Pill } from "../../components/ui/Pill";
import { StatCard } from "../../components/ui/StatCard";
import { Tabs } from "../../components/ui/Tabs";
import { useFrameVariant } from "../../hooks/useFrameVariant";
import { useSettings } from "../../hooks/useSettings";
import { displayValue, fmtAge, fmtTime } from "../../lib/glucose";
import type {
  Alert,
  Prediction,
  PredictionStatsResponse,
} from "../../lib/types";
import styles from "./AlertsScreen.module.css";

type TabId = "alerts" | "predictions";

const WINDOW_HOURS = 24 * 7;
const LIMIT = 200;

function toneForLevel(level: string): "urgent" | "warn" | "neutral" {
  if (level.endsWith("urgent_low") || level.endsWith("urgent_high"))
    return "urgent";
  if (level.endsWith("low") || level.endsWith("high")) return "warn";
  return "neutral";
}

function alertStatus(a: Alert): { label: string; tone?: "urgent" | "ok" } {
  if (a.acknowledged_at) return { label: "Acknowledged", tone: "ok" };
  if (a.fired_at) return { label: "Fired" };
  return { label: "Suppressed" };
}

function predictionOutcomeLabel(p: Prediction): string {
  if (!p.outcome) return "Pending";
  if (p.outcome === "confirmed") return "Confirmed";
  if (p.outcome === "false_positive") return "False positive";
  return "Superseded";
}

function predictionOutcomeTone(p: Prediction): "ok" | "warn" | "neutral" {
  if (p.outcome === "confirmed") return "ok";
  if (p.outcome === "false_positive") return "warn";
  return "neutral";
}

function fmtSeconds(s: number | null): string {
  if (s == null) return "—";
  if (s < 90) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  return `${(m / 60).toFixed(1)}h`;
}

export function AlertsScreen() {
  const { units } = useSettings();
  // Cycle 169: glass the prediction summary cards on the mobile frame
  // (Alerts is reachable on mobile via push deep-link); kiosk/web solid.
  const isMobile = useFrameVariant() === "mobile";
  const [tab, setTab] = useState<TabId>("alerts");
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [stats, setStats] = useState<PredictionStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cycle 28: when arriving via a push-notification tap (or the
  // `sugalarm://alerts?id=…` URL scheme) the URL carries the alert
  // id. Scroll the matching row into view + apply a ~1.5s pulse
  // class. If the id isn't in the current page, no error and no
  // highlight — user just lands on /alerts.
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("id");
  const [pulseId, setPulseId] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (tab === "alerts") {
      const r = await alertsApi.list(WINDOW_HOURS, LIMIT);
      if (r.ok) setAlerts(r.data.alerts);
      else setError(r.error.message);
    } else {
      const [list, summary] = await Promise.all([
        predictionsApi.list({ hours: WINDOW_HOURS, limit: LIMIT }),
        predictionsApi.stats(30),
      ]);
      if (list.ok) setPredictions(list.data.predictions);
      else setError(list.error.message);
      if (summary.ok) setStats(summary.data);
    }
    setLoading(false);
  }, [tab]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Cycle 28: after a load completes, look for the deep-link target
  // in the freshly-set list. Scroll + pulse for ~1.5s, then clear so
  // re-renders don't re-pulse on every interaction.
  useEffect(() => {
    if (!highlightId) return;
    if (tab !== "alerts") return;
    if (loading) return;
    const found = alerts.find((a) => a.id === highlightId);
    if (!found) return;
    const el = rowRefs.current.get(highlightId);
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    setPulseId(highlightId);
    const timer = window.setTimeout(() => setPulseId(null), 1500);
    return () => window.clearTimeout(timer);
  }, [highlightId, tab, loading, alerts]);

  const ackOne = async (id: string) => {
    const r = await alertsApi.acknowledge(id);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    void reload();
  };

  const ackAll = async () => {
    const r = await alertsApi.acknowledgeAll();
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    void reload();
  };

  const unackCount = alerts.filter((a) => !a.acknowledged_at).length;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div style={{ display: "flex", alignItems: "baseline" }}>
          <h2 className={styles.title}>Alerts</h2>
          <span className={styles.titleSub}>last 7 days</span>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Tabs<TabId>
            tabs={[
              { id: "alerts", label: "Alerts" },
              { id: "predictions", label: "Predictions" },
            ]}
            activeId={tab}
            onChange={setTab}
          />
          {tab === "alerts" && unackCount > 0 && (
            <Button variant="ghost" size="sm" onClick={ackAll}>
              Acknowledge all
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ color: "var(--zone-urgent)", fontSize: "var(--type-floor)" }}>{error}</div>
      )}

      {tab === "predictions" && stats && (
        <div className={styles.summaryRow}>
          <StatCard
            label="Total"
            value={stats.total_predictions}
            sub={`${stats.window_days}d window`}
            glass={isMobile}
          />
          <StatCard
            label="Pending"
            value={stats.pending}
            sub="awaiting horizon"
            glass={isMobile}
          />
          <StatCard
            label="Lead p50"
            value={fmtSeconds(stats.lead_time_seconds.p50)}
            sub="confirmed"
            glass={isMobile}
          />
          <StatCard
            label="Lead p90"
            value={fmtSeconds(stats.lead_time_seconds.p90)}
            glass={isMobile}
          />
          <StatCard
            label="Hit rate by level"
            value={
              <span style={{ display: "flex", gap: 8, fontSize: 14 }}>
                {(["predicted_urgent_low", "predicted_low", "predicted_high", "predicted_urgent_high"] as const).map(
                  (k) => {
                    const b = stats.by_level[k];
                    const v = b.hit_rate == null ? "—" : `${Math.round(b.hit_rate * 100)}%`;
                    return (
                      <span
                        key={k}
                        title={k.replace("predicted_", "")}
                        style={{ color: "var(--ink-2)" }}
                      >
                        {v}
                      </span>
                    );
                  },
                )}
              </span>
            }
            sub="ul / lo / hi / uh"
            glass={isMobile}
          />
        </div>
      )}

      {/* Body lists */}
      {loading ? (
        <div className={styles.empty}>Loading…</div>
      ) : tab === "alerts" ? (
        alerts.length === 0 ? (
          <div className={styles.empty}>
            No alerts in the last 7 days. Sugar Alarm fires on level
            transitions only — sustained out-of-range produces one row,
            not one per reading.
          </div>
        ) : (
          <div className={styles.list}>
            {alerts.map((a) => {
              const ts = new Date(a.triggered_at).getTime();
              const ageMin = Math.max(0, Math.floor((Date.now() - ts) / 60_000));
              const status = alertStatus(a);
              const isPulsing = pulseId === a.id;
              return (
                <div
                  key={a.id}
                  className={`${styles.row}${isPulsing ? ` ${styles.rowPulse}` : ""}`}
                  ref={(el) => {
                    if (el) rowRefs.current.set(a.id, el);
                    else rowRefs.current.delete(a.id);
                  }}
                >
                  <Pill tone={toneForLevel(a.level)}>
                    {a.level.replace("_", " ")}
                  </Pill>
                  <span className={styles.rowValue}>{displayValue(a.sgv, units)}</span>
                  <div className={styles.rowMeta}>
                    <span className={styles.rowTime}>
                      {fmtTime(a.triggered_at)} · {fmtAge(ageMin)}
                    </span>
                    <span className={styles.rowStatus}>{status.label}</span>
                  </div>
                  {!a.acknowledged_at ? (
                    <Button size="sm" variant="ghost" onClick={() => ackOne(a.id)}>
                      Acknowledge
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )
      ) : predictions.length === 0 ? (
        <div className={styles.empty}>
          No predictions in the last 7 days. The predictor only emits
          rows on edge transitions — quiet trends produce nothing.
        </div>
      ) : (
        <div className={styles.list}>
          {predictions.map((p) => {
            const ts = new Date(p.predicted_at).getTime();
            const ageMin = Math.max(0, Math.floor((Date.now() - ts) / 60_000));
            return (
              <div key={p.id} className={styles.row}>
                <Pill tone={toneForLevel(p.predicted_level)}>
                  {p.predicted_level.replace("predicted_", "").replace("_", " ")}
                </Pill>
                <span className={styles.rowValue}>{displayValue(p.predicted_sgv, units)}</span>
                <div className={styles.rowMeta}>
                  <span className={styles.rowTime}>
                    {fmtTime(p.predicted_at)} · {fmtAge(ageMin)} · horizon{" "}
                    {p.horizon_minutes}m
                  </span>
                  <span className={styles.rowStatus}>
                    slope{" "}
                    <span className="tnum">
                      {p.slope_mgdl_per_min >= 0 ? "+" : ""}
                      {p.slope_mgdl_per_min.toFixed(2)}
                    </span>{" "}
                    · R²{" "}
                    <span className="tnum">
                      {p.slope_r_squared != null
                        ? p.slope_r_squared.toFixed(2)
                        : "—"}
                    </span>
                  </span>
                </div>
                <Pill tone={predictionOutcomeTone(p)}>
                  {predictionOutcomeLabel(p)}
                </Pill>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
